import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Divider, Dropdown, Tag } from "antd";
import { useSearchParams } from "react-router-dom";
import { useLocation, useNavigate } from "react-router";
import { X, ChevronDown, Trash2 } from "lucide-react";
import TrialModal from "../../common/TrialModal";

const EXCLUDED_KEYS = ["page", "size", "layout"];

const splitValues = (value = "") =>
  value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);

const LeadNavigation = ({ onFilterChange }) => {
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const users = useSelector((state) => state.UserManagementReducer.users);
  const leadStatusList = useSelector(
    (state) => state.leadListingReducer.leadStatusList,
  );
  const employeeCountList = useSelector(
    (state) => state.leadListingReducer.employeeCountList,
  );
  const industryList = useSelector(
    (state) => state.leadListingReducer.industryList,
  );

  const allParams = useMemo(() => {
    return Object.fromEntries(
      [...searchParams.entries()].filter(
        ([key]) => !EXCLUDED_KEYS.includes(key),
      ),
    );
  }, [searchParams]);

  const hasOtherFilters = Object.keys(allParams).length > 0;

  const formatValue = (key, value) => {
    const ids = splitValues(value);

    switch (key) {
      case "ownerName": {
        const user = users?.content?.find((u) => u.id === Number(value));
        return user ? `${user.firstName} ${user.lastName}` : value;
      }

      case "Lead Status":
        return leadStatusList
          ?.filter((d) => ids.includes(d.id.toString()))
          ?.map((d) => d.text)
          .join(", ");

      case "Employee Size":
        return employeeCountList
          ?.filter((d) => ids.includes(d.id.toString()))
          ?.map((d) => d.name)
          .join(", ");

      case "Industry":
        return industryList
          ?.filter((d) => ids.includes(d.id.toString()))
          ?.map((d) => d.name)
          .join(", ");

      default:
        return value;
    }
  };

  return (
    <div>
      {hasOtherFilters && (
        <div className="tw-flex tw-items-center">
          {Object.entries(allParams).length && (
            <>
              <Dropdown
                trigger={["click", "hover"]}
                placement="bottomLeft"
                overlay={
                  <div className="tw-w-[260px] tw-bg-white tw-rounded-lg tw-shadow-lg tw-p-3">
                    <div className="tw-font-semibold tw-text-xs tw-mb-2 tw-text-center">
                      All Active Filters ({Object.entries(allParams).length})
                    </div>

                    {Object.entries(allParams).map(([key, value]) => (
                      <div
                        key={key}
                        className="tw-flex tw-justify-between tw-items-center tw-py-2"
                      >
                        <span className="tw-text-xs tw-text-sub-text">
                          {key}:{" "}
                          <b className="tw-text-text-primary">
                            {formatValue(key, value)}
                          </b>
                        </span>
                        <X
                          size={14}
                          className="tw-cursor-pointer tw-text-slate-500 hover:tw-text-red-500"
                          onClick={() => onFilterChange(key)}
                        />
                      </div>
                    ))}

                    <Divider className="tw-my-2" />

                    <div
                      className="tw-text-[#FF1115] tw-text-xs tw-font-semibold tw-cursor-pointer tw-flex tw-items-center tw-justify-center tw-gap-2"
                      onClick={() =>
                        navigate(location.pathname, { replace: true })
                      }
                    >
                      <Trash2 size={14} />
                      Clear all filters
                    </div>
                  </div>
                }
              >
                <Tag
                  className="!tw-border-0 tw-cursor-pointer !tw-py-1.5 !tw-px-3.5 !tw-rounded-full !tw-bg-[#EFEAFF] !tw-text-xs !tw-font-bold !tw-text-[#5D44FF] tw-flex tw-items-center tw-gap-1.5 tw-shadow-sm hover:!tw-bg-[#e5dfff] tw-transition-colors"
                >
                  Active Filter(s): + {Object.entries(allParams).length}
                  <ChevronDown size={14} className="tw-text-[#5D44FF]" />
                </Tag>
              </Dropdown>

              <div
                className="tw-text-[#FF1115] tw-text-xs tw-font-semibold tw-whitespace-nowrap tw-ml-2 tw-cursor-pointer hover:tw-underline"
                onClick={() => navigate(location.pathname, { replace: true })}
              >
                Clear all
              </div>
            </>
          )}
        </div>
      )}

      {showModal && (
        <TrialModal
          openModal={showModal}
          closeModal={() => setShowModal(false)}
          redirectToSignup={() => {
            setShowModal(false);
            navigate("/signup");
          }}
          redirect
        />
      )}
    </div>
  );
};

export default LeadNavigation;
